import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, Undo2, Home } from 'lucide-react';

const font = "'Inria Serif', serif";

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_) {
    return iso;
  }
};

export default function AccountDeletedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const finalizeAt = location.state?.finalizeAt;
  const formattedDate = formatDate(finalizeAt);

  // Resolve which "privacy" route to send the user to based on stored role.
  let privacyPath = '/guest/settings/privacy';
  let homePath = '/guest/home';
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const role = u?.role;
    if (role === 'host') {
      privacyPath = '/host/settings/privacy';
      homePath = '/host/home';
    } else if (role === 'venue_owner') {
      privacyPath = '/venue/settings';
      homePath = '/venue/home';
    } else if (role === 'admin' || role === 'staff') {
      privacyPath = '/staff-admin/profile';
      homePath = '/';
    }
  } catch (_) {}

  useEffect(() => {
    document.title = 'Account deletion scheduled — Allure';
  }, []);

  return (
    <div
      data-testid="account-deleted-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 78,
          height: 78,
          borderRadius: '50%',
          background: '#FFF8E8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 18,
          boxShadow: '0 0 0 8px rgba(168, 106, 0, 0.06)',
        }}
      >
        <Clock size={44} color="#A86A00" />
      </div>

      <h1 style={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: '#111', margin: 0 }}>
        Deletion scheduled
      </h1>
      {formattedDate ? (
        <p style={{ fontFamily: font, fontSize: 16, color: '#444', maxWidth: 460, marginTop: 14, lineHeight: 1.55 }}>
          Your account is set to be permanently deleted on{' '}
          <strong>{formattedDate}</strong>.
        </p>
      ) : (
        <p style={{ fontFamily: font, fontSize: 16, color: '#444', maxWidth: 460, marginTop: 14, lineHeight: 1.55 }}>
          Your account will be permanently deleted in 14 days.
        </p>
      )}

      <p style={{ fontFamily: font, fontSize: 14, color: '#666', maxWidth: 460, marginTop: 14, lineHeight: 1.55 }}>
        Changed your mind? You can <strong>cancel any time</strong> before that date — your data
        stays untouched until then. Continue using Allure as normal in the meantime.
      </p>

      <p style={{ fontFamily: font, fontSize: 13, color: '#999', marginTop: 16 }}>
        Read our full{' '}
        <a href="/delete-account" style={{ color: '#703FF6' }}>
          deletion policy
        </a>
        .
      </p>

      <div style={{ display: 'flex', gap: 10, marginTop: 28, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={() => navigate(privacyPath, { replace: true })}
          data-testid="account-deleted-cancel-button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 22px',
            background: '#A86A00',
            color: '#fff',
            fontFamily: font,
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
          }}
        >
          <Undo2 size={16} /> Cancel deletion
        </button>
        <button
          type="button"
          onClick={() => navigate(homePath, { replace: true })}
          data-testid="account-deleted-home-button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 22px',
            background: '#111',
            color: '#fff',
            fontFamily: font,
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
          }}
        >
          <Home size={16} /> Continue using Allure
        </button>
      </div>
    </div>
  );
}
